import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatDate, formatMoney, formatQty } from '../api';
import { Badge, Card, Empty, Loading, Notice, TextField } from '../components/ui';
import type { Partner } from '../types';

interface Posten {
  id: string;
  sku: string;
  article_name: string;
  zahler_id: string;
  zahler_name: string;
  bezeichnung: string;
  referenz: string | null;
  verkauft_am: string;
  menge: number;
  betrag_je_einheit_cents: number;
  betrag_cents: number;
  status: 'offen' | 'angefordert' | 'bezahlt' | 'abgelehnt';
  angefordert_am: string | null;
  angefordert_referenz: string | null;
  bezahlt_am: string | null;
  bezahlt_cents: number | null;
  differenz_cents: number | null;
  notiz: string | null;
  erledigt_von_name: string | null;
  belegnummer: string | null;
}

interface Zahlerstand {
  zahler_id: string;
  zahler_name: string;
  offen_anzahl: number;
  offen_cents: number;
  angefordert_anzahl: number;
  angefordert_cents: number;
  bezahlt_cents: number;
  differenz_cents: number;
}

const STATUS: Record<Posten['status'], { kind: 'warn' | 'info' | 'ok' | 'bad'; text: string }> = {
  offen: { kind: 'warn', text: 'offen' },
  angefordert: { kind: 'info', text: 'angefordert' },
  bezahlt: { kind: 'ok', text: 'bezahlt' },
  abgelehnt: { kind: 'bad', text: 'abgelehnt' },
};

const heute = () => new Date().toISOString().slice(0, 10);
const vorTagen = (n: number) => new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);

export function AvuPage() {
  const [posten, setPosten] = useState<Posten[] | null>(null);
  const [staende, setStaende] = useState<Zahlerstand[]>([]);
  const [partner, setPartner] = useState<Partner[]>([]);

  const [zahler, setZahler] = useState('');
  const [status, setStatus] = useState('');
  const [von, setVon] = useState(vorTagen(90));
  const [bis, setBis] = useState(heute());

  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set());
  const [referenz, setReferenz] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    const frage = new URLSearchParams({ von, bis });
    if (zahler) frage.set('zahlerId', zahler);
    if (status) frage.set('status', status);
    try {
      const daten = await api<{ posten: Posten[]; staende: Zahlerstand[] }>(
        `/api/articles/avu/posten?${frage.toString()}`,
      );
      setPosten(daten.posten);
      setStaende(daten.staende);
      setGewaehlt(new Set());
    } catch (err) {
      setError(errorText(err));
    }
  }, [zahler, status, von, bis]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void api<Partner[]>('/api/partners').then(setPartner).catch(() => setPartner([]));
  }, []);

  function umschalten(id: string) {
    setGewaehlt((alt) => {
      const neu = new Set(alt);
      if (neu.has(id)) neu.delete(id);
      else neu.add(id);
      return neu;
    });
  }

  function alleUmschalten(sichtbar: Posten[]) {
    setGewaehlt((alt) =>
      alt.size === sichtbar.length ? new Set() : new Set(sichtbar.map((p) => p.id)),
    );
  }

  async function setze(neuerStatus: 'angefordert' | 'bezahlt' | 'abgelehnt') {
    setBusy(true);
    setError('');
    try {
      const ergebnis = await api<{ geaendert: number }>('/api/articles/avu/posten/status', {
        method: 'POST',
        body: {
          ids: [...gewaehlt],
          status: neuerStatus,
          referenz: referenz.trim() || null,
        },
      });
      setMessage(`${ergebnis.geaendert} Posten auf "${neuerStatus}" gesetzt.`);
      setReferenz('');
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!posten) return error ? <Notice kind="error">{error}</Notice> : <Loading />;

  const waehlbar = posten.filter((p) => p.status !== 'bezahlt');
  const summeGewaehlt = posten
    .filter((p) => gewaehlt.has(p.id))
    .reduce((s, p) => s + p.betrag_cents, 0);

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <Card title="Auswahl">
        <div className="form-grid">
          <label className="field">
            <span>Hersteller / Zahler</span>
            <select value={zahler} onChange={(e) => setZahler(e.target.value)}>
              <option value="">alle</option>
              {partner.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Stand</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">alle</option>
              <option value="offen">offen</option>
              <option value="angefordert">angefordert</option>
              <option value="bezahlt">bezahlt</option>
              <option value="abgelehnt">abgelehnt</option>
            </select>
          </label>
          <TextField label="Verkauft von" value={von} onChange={setVon} hint="JJJJ-MM-TT" />
          <TextField label="bis" value={bis} onChange={setBis} />
        </div>
      </Card>

      <Card title="Stand je Hersteller" padded={false}>
        <div className="body small muted" style={{ paddingBottom: 0 }}>
          Die Zahl, mit der man beim Hersteller anruft. <strong>Offen</strong> heisst verkauft,
          aber noch nicht abgerufen — das ist Geld, das Ihnen zusteht und niemand geltend gemacht
          hat.
        </div>
        {staende.length === 0 ? (
          <Empty>Im Zeitraum ist keine Unterstützung angefallen.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Hersteller</th>
                  <th className="num">Offen</th>
                  <th className="num">Angefordert</th>
                  <th className="num">Bezahlt</th>
                  <th className="num">Differenz</th>
                </tr>
              </thead>
              <tbody>
                {staende.map((s) => (
                  <tr key={s.zahler_id}>
                    <td>{s.zahler_name}</td>
                    <td className="num">
                      <strong>{formatMoney(s.offen_cents)}</strong>
                      <div className="small muted">{s.offen_anzahl} Posten</div>
                    </td>
                    <td className="num">
                      {formatMoney(s.angefordert_cents)}
                      <div className="small muted">{s.angefordert_anzahl} Posten</div>
                    </td>
                    <td className="num muted">{formatMoney(s.bezahlt_cents)}</td>
                    <td className="num">
                      {s.differenz_cents === 0 ? (
                        <span className="muted">—</span>
                      ) : (
                        <Badge kind={s.differenz_cents < 0 ? 'bad' : 'ok'}>
                          {formatMoney(s.differenz_cents)}
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {gewaehlt.size > 0 && (
        <Card title={`${gewaehlt.size} Posten gewählt · ${formatMoney(summeGewaehlt)}`}>
          <div className="form-grid">
            <TextField
              label="Referenz"
              value={referenz}
              onChange={setReferenz}
              placeholder="Sammelabruf 2026-09 / Gutschrift GS-4711"
              hint="wird an allen gewählten Posten vermerkt"
            />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => setze('angefordert')} disabled={busy}>
              Beim Hersteller angefordert
            </button>
            <button className="primary" onClick={() => setze('bezahlt')} disabled={busy}>
              Geld ist da — abhaken
            </button>
            <button onClick={() => setze('abgelehnt')} disabled={busy}>
              Abgelehnt
            </button>
          </div>
          <div className="small muted" style={{ marginTop: 10 }}>
            Beim Abhaken gilt der erwartete Betrag als eingegangen. Wurde gekürzt, tragen Sie den
            tatsächlichen Betrag am einzelnen Posten nach — die Differenz bleibt dann sichtbar.
          </div>
        </Card>
      )}

      <Card title="Kassierte Artikel mit Unterstützung" padded={false}>
        {posten.length === 0 ? (
          <Empty>Im Zeitraum wurde kein Artikel mit Unterstützung verkauft.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 34 }}>
                    <input
                      type="checkbox"
                      checked={gewaehlt.size > 0 && gewaehlt.size === waehlbar.length}
                      onChange={() => alleUmschalten(waehlbar)}
                      aria-label="Alle offenen wählen"
                    />
                  </th>
                  <th>Verkauft</th>
                  <th>Artikel</th>
                  <th>Hersteller / Aktion</th>
                  <th className="num">Menge</th>
                  <th className="num">je Stück</th>
                  <th className="num">Betrag</th>
                  <th>Stand</th>
                </tr>
              </thead>
              <tbody>
                {posten.map((p) => (
                  <tr key={p.id}>
                    <td>
                      {p.status !== 'bezahlt' && (
                        <input
                          type="checkbox"
                          checked={gewaehlt.has(p.id)}
                          onChange={() => umschalten(p.id)}
                          aria-label={`${p.sku} wählen`}
                        />
                      )}
                    </td>
                    <td className="small">
                      {formatDate(p.verkauft_am)}
                      {p.belegnummer && <div className="muted mono">{p.belegnummer}</div>}
                    </td>
                    <td className="small">
                      <span className="mono">{p.sku}</span> {p.article_name}
                    </td>
                    <td className="small">
                      {p.zahler_name}
                      <div className="muted">
                        {p.bezeichnung}
                        {p.referenz && ` · ${p.referenz}`}
                      </div>
                    </td>
                    <td className="num">{formatQty(p.menge)}</td>
                    <td className="num muted">{formatMoney(p.betrag_je_einheit_cents)}</td>
                    <td className="num">
                      <strong>{formatMoney(p.betrag_cents)}</strong>
                      {p.differenz_cents !== null && p.differenz_cents !== 0 && (
                        <div className="small" style={{ color: 'var(--bad)' }}>
                          {formatMoney(p.differenz_cents)} Differenz
                        </div>
                      )}
                    </td>
                    <td className="small">
                      <Badge kind={STATUS[p.status].kind}>{STATUS[p.status].text}</Badge>
                      {p.angefordert_referenz && (
                        <div className="muted">{p.angefordert_referenz}</div>
                      )}
                      {p.bezahlt_am && <div className="muted">am {formatDate(p.bezahlt_am)}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
